import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Badge, Button, EmptyState } from '@teach/ui'
import { BackButton } from '@/components/common/BackButton'
import { UnderlineTabs } from '@/components/common/UnderlineTabs'
import { ModerationPoller } from '@/features/moderation/ModerationPoller'
import { useAppDispatch } from '@/store/hooks'
import { useLanguage } from '@/hooks/useLanguage'
import { courseDisplayName } from '@/lib/localizedText'
import type { CourseWithoutBlocks } from '@/types/course'
import { resetEditor, seedContentLang, setSettingsLang } from '../../editorSlice'
import { seedLangFor } from '../studio/seedContentLang'
import { useEditorCourseGate } from '../studio/useEditorCourseGate'
import { CourseSettingsContent } from './CourseSettingsContent'
import { CourseSettingsSkeleton } from './CourseSettingsSkeleton'
import { TABS } from './tabs'
import type { TabKey } from './tabs'
import type { ViolationGo } from '../publish/violationPlace'
import { useCourseSettingsForm } from './useCourseSettingsForm'
import { messagesNow, useMessages } from '@/i18n'
import { messages } from './messages'
import type { SettingsPageState } from './settingsPageState'

const PageBody = ({ course }: { course: CourseWithoutBlocks }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { language } = useLanguage()
  const m = useMessages(messages)
  const pageState = useLocation().state as SettingsPageState | null
  const fromEditor = pageState?.from === 'editor'
  const [tab, setTab] = useState<TabKey>(
    () => TABS.find((t) => t.key === pageState?.tab)?.key ?? 'basic',
  )
  const { local, setLocal, save, flushPendingChanges } = useCourseSettingsForm(course, true)

  // Несохранённое поле (ушли со страницы, не потеряв фокус) уходит на сервер.
  // `flushPendingChanges` — новое замыкание на каждый рендер: в зависимостях эффекта
  // оно сбрасывало бы поле на сервер при каждой букве, поэтому читаем его через реф
  // (приём из ImageUpload). Объявлен ВЫШЕ эффекта с `resetEditor`: cleanup идут в
  // порядке объявления, и flush должен успеть до сброса стора редактора.
  const flushRef = useRef(flushPendingChanges)
  useEffect(() => {
    flushRef.current = flushPendingChanges
  })
  useEffect(() => () => flushRef.current(), [])

  // Настройки читают язык правки и «засеянный» язык из стора редактора: страница
  // живёт вне редактора, поэтому засевает их сама и убирает за собой.
  useEffect(() => {
    dispatch(seedContentLang(seedLangFor(course)))
    if (pageState?.lang) dispatch(setSettingsLang(pageState.lang))
    return () => void dispatch(resetEditor())
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно по id, а не по объекту курса
  }, [course.id, dispatch])

  // Урок и блок правятся только в редакторе: туда и уходим, а выбор он разберёт из
  // параметров (`EditorWorkspace`). Несохранённое поле уйдёт flush'ем на размонтировании.
  const goToViolation = (target: ViolationGo) => {
    if (target.kind === 'course') {
      // Незаполненное поле на другом языке: иначе поля открывались на языке правки, где
      // всё заполнено, и пропуска не было видно.
      if (target.lang) dispatch(setSettingsLang(target.lang))
      setTab(target.tab ?? 'basic')
      return
    }
    const block = target.kind === 'block' ? `&block=${target.blockId}` : ''
    const lang = target.lang ? `&lang=${target.lang}` : ''
    navigate(`/editor/${course.id}?lesson=${target.lessonId}${block}${lang}`)
  }

  const tabs = TABS.map((t) => ({
    id: t.key,
    label: t.label,
    icon: t.icon,
    extra: t.soon ? <Badge variant="neutral">{m.soon}</Badge> : null,
  }))

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 px-4 py-8 xl:max-w-6xl wide:max-w-7xl">
      {/* Название читаем из черновика формы, а не из серверного курса: серверный приезжает
          только после сохранения и перечитывания (около двух секунд задержки). */}
      <div className="flex items-center gap-2">
        {/* Пришли из редактора (шестерёнка на телефоне) — «назад» шагом по истории, а не
            новым переходом в редактор: иначе история «редактор → настройки → редактор»,
            и домик редактора (тоже шаг назад) снова открывал настройки — по кругу. */}
        {fromEditor ? (
          <BackButton to={-1} label={m.backToEditor} />
        ) : (
          <BackButton to="/dashboard" label={m.backToMyCourses} />
        )}
        <h1
          title={courseDisplayName(local, language)}
          className="line-clamp-2 min-w-0 text-xl font-semibold md:line-clamp-none md:text-3xl"
        >
          {courseDisplayName(local, language)}
        </h1>
      </div>
      <UnderlineTabs tabs={tabs} value={tab} onChange={setTab} />
      <div className="relative pt-4">
        <CourseSettingsContent
          course={course}
          activeTab={tab}
          local={local}
          setLocal={setLocal}
          save={save}
          showHeading={false}
          onNavigate={goToViolation}
        />
      </div>
    </div>
  )
}

/**
 * Страница «Настройки курса» — те же вкладки и секции, что в окне редактора,
 * но отдельным экраном, как раздел настроек профиля. Курс читается тем же
 * гейтом, что и редактор: из черновика, если он есть, иначе правка легла бы не в
 * ту версию.
 */
export const CourseSettingsPage = ({ courseId }: { courseId: number }) => {
  const { course, draftOfId, courseMissing, loadFailed, retry } = useEditorCourseGate(courseId)
  const navigate = useNavigate()
  const m = useMessages(messages)

  useEffect(() => {
    if (draftOfId) navigate(`/course-settings/${draftOfId}`, { replace: true })
  }, [draftOfId, navigate])

  useEffect(() => {
    if (courseMissing) {
      toast.error(messagesNow(messages).courseNotFound)
      navigate('/dashboard', { replace: true })
    }
  }, [courseMissing, navigate])

  return (
    <>
      {course !== undefined && !draftOfId ? (
        <PageBody course={course} />
      ) : !loadFailed ? (
        <CourseSettingsSkeleton />
      ) : (
        <div className="flex min-h-[60vh] items-center justify-center">
          <EmptyState
            size="lg"
            title={m.loadFailedTitle}
            description={m.loadFailedDescription}
            action={
              <Button variant="outline" onClick={retry}>
                {m.retry}
              </Button>
            }
          />
        </div>
      )}
      <ModerationPoller courseId={courseId} />
    </>
  )
}
